import { useEffect, useRef, useCallback } from 'react';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from '../../i18n';
import { getPendingCount } from '../../lib/offline/offlineDb';

const CHECK_INTERVAL = 60 * 60 * 1000; // 1 hour
const SYNC_POLL_INTERVAL = 2_000;
const SYNC_POLL_MAX_WAIT = 60_000;

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/meta.json?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data.version ?? null;
  } catch {
    return null;
  }
}

export function PwaUpdatePrompt() {
  const toast = useToast();
  const { t: strings } = useTranslation();
  const updateTriggered = useRef(false);

  const currentVersion = __APP_VERSION__.sha;

  const reloadWhenSyncIdle = useCallback(() => {
    if (updateTriggered.current) return;
    updateTriggered.current = true;

    const start = Date.now();

    const check = async () => {
      const pending = await getPendingCount();

      if (pending === 0 || Date.now() - start > SYNC_POLL_MAX_WAIT) {
        window.location.reload();
        return;
      }

      setTimeout(check, SYNC_POLL_INTERVAL);
    };

    check();
  }, []);

  const checkForUpdate = useCallback(async () => {
    if (updateTriggered.current) return;
    const remoteVersion = await fetchRemoteVersion();
    if (!remoteVersion || remoteVersion === currentVersion) return;

    toast.show(strings.common.pwaUpdateAvailable, 'info', 10_000);
    setTimeout(reloadWhenSyncIdle, 5_000);
  }, [currentVersion, toast, strings, reloadWhenSyncIdle]);

  useEffect(() => {
    const timer = setInterval(checkForUpdate, CHECK_INTERVAL);
    const onFocus = () => checkForUpdate();
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [checkForUpdate]);

  return null;
}
